/**
 * @fileoverview Projecting the shipped registry into runtime state.
 *
 * The base registry is the same for everybody; what a given administrator sees
 * is that registry filtered by their permissions, the services that are up, and
 * whatever each page says about itself. Foundry calls this hydration, and the
 * recovered pipeline is explicit about the ORDER:
 *
 *     platform → athena → platform-override → extension-restrictions →
 *     required-services-and-dynamic-signals → space-or-gatekeeper-permissions →
 *     supersession → display-name-ordering
 *
 *     ../palantir/mcp/foundry/evidence/ui/
 *       control-panel-default-extension-registry-6.521.57.json § hydrationPipeline
 *
 * ORDER IS NOT COSMETIC. The cheap, local, synchronous predicates run before
 * the ones that await a service, so a page excluded by permission never costs a
 * network call. Supersession runs LAST, after visibility, because a superseded
 * page must lose to its replacement whether or not the replacement is visible
 * to this viewer — resolve it early and a locked replacement silently unhides
 * the page it replaced.
 *
 * WHICH STAGES ARE REAL HERE. `platform` and `platform-override` gate on which
 * Palantir platform is serving (apollo / foundry / gotham); `athena` gates on a
 * service we do not run. One deployment, one platform, no Athena — so those
 * three stages have nothing to decide and are absent rather than stubbed. The
 * five that remain are implemented, in this order, and the pipeline is written
 * so adding a stage back is an insertion rather than a redesign.
 *
 * THE TIMEOUT IS LOAD-BEARING. Foundry bounds the dynamic checks at 10s and
 * ships distinct messages for each — "Extension discoverability check timed
 * out", "Extension enabled check timed out". A hung availability probe must
 * degrade to a decision, not to a Control Panel that never finishes loading;
 * the fallbacks below say which decision.
 */

import type {
  ControlPanelExtensionDefinition,
  ExtensionType,
  HydratedExtension,
} from "./ExtensionDefinition";

/** Recovered value: `hydrationPipeline.timeoutMilliseconds`. */
export const HYDRATION_TIMEOUT_MS = 10_000;

/**
 * What the host knows about the current viewer and deployment.
 *
 * `operations` is the flattened set of Cedar operations this principal holds;
 * `availableServices` the backends currently answering. Both are supplied by
 * the caller rather than fetched here, so this module stays a pure projection
 * and is testable without a network.
 */
export interface HydrationContext {
  readonly operations: ReadonlySet<string>;
  readonly availableServices: ReadonlySet<string>;
  /**
   * Scopes that exist in this context. An extension whose `type` is not
   * resolvable here is not applicable — a per-space page with no space
   * selected has nothing to configure.
   */
  readonly resolvableTypes: ReadonlySet<ExtensionType>;
  /**
   * Ids an operator has explicitly turned off for this deployment. Foundry's
   * "extension restrictions" stage; entitlement there, configuration here, same
   * position in the order.
   */
  readonly restrictedIds?: ReadonlySet<string>;
}

/**
 * Races a predicate against the timeout.
 *
 * `onTimeout` differs per caller and that is the point: a discoverability
 * check that hangs resolves to VISIBLE (show it, let the page report its own
 * error) while an enablement check that hangs resolves to LOCKED (do not invite
 * an administrator into a page whose backing service is not answering). Failing
 * the same way in both places would be simpler and wrong in one of them.
 */
async function withTimeout(
  probe: () => Promise<boolean>,
  onTimeout: boolean,
  timeoutMs: number,
): Promise<{ ok: boolean; timedOut: boolean }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<{ ok: boolean; timedOut: boolean }>((resolve) => {
    timer = setTimeout(() => resolve({ ok: onTimeout, timedOut: true }), timeoutMs);
  });
  try {
    return await Promise.race([
      probe().then(
        (ok) => ({ ok, timedOut: false }),
        // A predicate that THROWS is not a predicate that said no. Treat it
        // like a timeout so the two failure modes get the same conservative
        // answer instead of an unhandled rejection.
        () => ({ ok: onTimeout, timedOut: true }),
      ),
      timeout,
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function holdsOperations(
  definition: ControlPanelExtensionDefinition,
  held: ReadonlySet<string>,
): boolean {
  const { conjunctiveOperations, disjunctiveOperations } = definition.visibility;
  for (const op of conjunctiveOperations) if (!held.has(op)) return false;
  if (disjunctiveOperations.length === 0) return true;
  return disjunctiveOperations.some((op) => held.has(op));
}

/**
 * Hydrate the base registry for one viewer.
 *
 * Returns every definition — including the ones that are not discoverable —
 * because the caller decides what to do with them. All Settings drops them;
 * the router needs them to tell "no such page" apart from "not for you", which
 * are different HTTP answers and different things to say to a person.
 */
export async function hydrateExtensionRegistry(
  registry: readonly ControlPanelExtensionDefinition[],
  context: HydrationContext,
  timeoutMs: number = HYDRATION_TIMEOUT_MS,
): Promise<readonly HydratedExtension[]> {
  const hidden = (definition: ControlPanelExtensionDefinition, reason: string): HydratedExtension => ({
    definition,
    isDiscoverable: false,
    isVisible: false,
    reason,
  });

  const hydrated = await Promise.all(
    registry.map(async (definition): Promise<HydratedExtension> => {
      // ── extension-restrictions ──────────────────────────────────────────
      if (context.restrictedIds?.has(definition.id)) {
        return hidden(definition, "not entitled (extension restrictions)");
      }

      // ── context: is this extension's scope resolvable at all? ───────────
      if (!context.resolvableTypes.has(definition.type)) {
        return hidden(definition, `no ${definition.type} in context`);
      }

      // ── required services ───────────────────────────────────────────────
      const missing = definition.visibility.requiredServices.filter(
        (s) => !context.availableServices.has(s),
      );
      if (missing.length > 0) {
        return hidden(definition, `missing required services: ${missing.join(", ")}`);
      }

      // ── dynamic signals ─────────────────────────────────────────────────
      //
      // Both probes run concurrently: they are independent, and serialising
      // them would make the worst case two timeouts rather than one.
      const [discoverable, enabled] = await Promise.all([
        definition.visibility.dynamicDiscoverability
          ? withTimeout(definition.visibility.dynamicDiscoverability, true, timeoutMs)
          : Promise.resolve({ ok: true, timedOut: false }),
        definition.visibility.dynamicEnablement
          ? withTimeout(definition.visibility.dynamicEnablement, false, timeoutMs)
          : Promise.resolve({ ok: true, timedOut: false }),
      ]);

      if (!discoverable.ok) {
        return hidden(
          definition,
          discoverable.timedOut
            ? "Extension discoverability check timed out"
            : "not discoverable in this context",
        );
      }

      // ── permissions ─────────────────────────────────────────────────────
      //
      // Missing an operation LOCKS rather than hides: the page stays in All
      // Settings behind "Show locked settings", which is how an administrator
      // discovers what they would need to be granted.
      if (!holdsOperations(definition, context.operations)) {
        return {
          definition,
          isDiscoverable: true,
          isVisible: false,
          reason: `missing required operations: ${definition.visibility.conjunctiveOperations.join(", ")}`,
        };
      }

      if (!enabled.ok) {
        return {
          definition,
          isDiscoverable: true,
          isVisible: false,
          reason: enabled.timedOut ? "Extension enabled check timed out" : "not enabled",
        };
      }

      /*
        NO COMPONENT MEANS LISTED BUT NOT OPENABLE. The registry names what the
        platform can configure; ~80 of those we have not built. They stay
        discoverable — All Settings shows them greyed behind "Show locked
        settings" — because an administrator who learns a capability exists and
        is not yet available is better served than one shown a shorter list and
        left to conclude it does not exist.
      */
      if (!definition.component) {
        return {
          definition,
          isImplemented: false,
          isDiscoverable: true,
          isVisible: false,
          reason: "Not available in this deployment yet",
        };
      }

      return { definition, isImplemented: true, isDiscoverable: true, isVisible: true };
    }),
  );

  // ── supersession ──────────────────────────────────────────────────────
  //
  // Last, and over the hydrated set: a page is superseded when its replacement
  // is DISCOVERABLE, not when the replacement merely exists. Otherwise a
  // replacement that this deployment does not have would take the old page down
  // with it and leave the setting unreachable.
  const discoverableIds = new Set(hydrated.filter((h) => h.isDiscoverable).map((h) => h.definition.id));
  const afterSupersession = hydrated.map((h) =>
    h.definition.supersededBy && discoverableIds.has(h.definition.supersededBy)
      ? { ...h, isDiscoverable: false, isVisible: false, reason: `superseded by ${h.definition.supersededBy}` }
      : h,
  );

  // ── display-name-ordering ─────────────────────────────────────────────
  //
  // The registry's array order is authoring order and means nothing to a
  // reader. `localeCompare` rather than `<` so accented names sort where a
  // person expects instead of after "Z".
  return [...afterSupersession].sort((a, b) =>
    a.definition.displayName.localeCompare(b.definition.displayName),
  );
}
